import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, XCircle, AlertCircle, Users } from "lucide-react";

export type SwapStatus = 'open' | 'accepted' | 'manager_pending' | 'finalized' | 'cancelled' | 'expired';

interface SwapStatusBadgeProps {
  status: SwapStatus;
  className?: string;
}

export function SwapStatusBadge({ status, className }: SwapStatusBadgeProps) {
  const getStatusConfig = (status: SwapStatus) => {
    switch (status) {
      case 'open':
        return {
          label: 'Pending',
          icon: Clock,
          variant: 'secondary' as const,
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        };
      case 'accepted':
        return {
          label: 'Accepted',
          icon: CheckCircle,
          variant: 'secondary' as const,
          className: 'bg-blue-100 text-blue-800 border-blue-200',
        };
      case 'manager_pending':
        return {
          label: 'Awaiting Approval',
          icon: Users,
          variant: 'secondary' as const,
          className: 'bg-purple-100 text-purple-800 border-purple-200',
        };
      case 'finalized':
        return {
          label: 'Completed',
          icon: CheckCircle,
          variant: 'secondary' as const,
          className: 'bg-green-100 text-green-800 border-green-200',
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          icon: XCircle,
          variant: 'secondary' as const,
          className: 'bg-gray-100 text-gray-800 border-gray-200',
        };
      case 'expired':
        return {
          label: 'Expired',
          icon: AlertCircle,
          variant: 'secondary' as const,
          className: 'bg-red-100 text-red-800 border-red-200',
        };
      default:
        return {
          label: 'Unknown',
          icon: AlertCircle,
          variant: 'secondary' as const,
          className: 'bg-gray-100 text-gray-800 border-gray-200',
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge 
      variant={config.variant}
      className={`inline-flex items-center gap-1 text-xs font-medium ${config.className} ${className || ''}`}
      aria-label={`Swap status: ${config.label}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {config.label}
    </Badge>
  );
}
